import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["", "active", "past_due", "canceled", "canceled_by_admin_refund"];

export default function AdminStudents() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const res = await api.adminListStudents({ q, status, limit: 100 });
            setRows(res.students);
            setTotal(res.total);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="admin-students-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Students
                </div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Manage enrollments</h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Search, filter, unlock phases, cancel subscriptions, and issue force-refunds.
                </p>
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); load(); }}
                className="st-surface p-4 flex flex-col md:flex-row md:items-end gap-3"
                data-testid="admin-students-filters"
            >
                <div className="flex-1">
                    <label className="st-label">Search name or email</label>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" color="var(--st-silver-500)" />
                        <input
                            className="st-input"
                            style={{ paddingLeft: "2rem" }}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="jane@example.com"
                            data-testid="students-search-input"
                        />
                    </div>
                </div>
                <div className="md:w-56">
                    <label className="st-label">Subscription status</label>
                    <select
                        className="st-input"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        data-testid="students-status-filter"
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s || "All"}</option>
                        ))}
                    </select>
                </div>
                <button type="submit" className="btn-royal text-sm" data-testid="students-apply-filter">Apply</button>
            </form>

            <div className="st-surface overflow-hidden" data-testid="admin-students-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Student</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Plan</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Status</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Joined</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No students match.</td></tr>
                        )}
                        {rows.map((u) => (
                            <tr key={u.user_id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`student-row-${u.user_id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{u.name || "—"}</div>
                                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>{u.email}</div>
                                </td>
                                <td className="px-4 py-3 text-sm">{u.plan || "—"}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span
                                        className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                        style={{
                                            background: u.subscription_status === "active" ? "#ECFDF3"
                                                : u.subscription_status === "past_due" ? "#FFFAEB" : "var(--st-silver-100)",
                                            color: u.subscription_status === "active" ? "#067647"
                                                : u.subscription_status === "past_due" ? "#B54708" : "var(--st-ink-muted)",
                                        }}
                                    >
                                        {u.subscription_status || "none"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <Link to={`/admin/students/${u.user_id}`} className="link-royal text-sm" data-testid={`open-student-${u.user_id}`}>
                                        Open →
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                Showing {rows.length} of {total} matching students.
            </p>
        </section>
    );
}
